import { Tooltip, TooltipContent, TooltipTrigger } from "@elit-hair/elit-ui";
import { MouseEvent, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { TbPhone, TbPhoneCalling, TbPhoneOff } from "react-icons/tb";

import { Button } from "@/components/ui/button";
import { CALL_STATUS } from "@/constants/call";
import { cn } from "@/lib/utils";
import { useCall } from "@/providers/hooks/use-call";
import { CallStatus } from "@/types/call.types";

export type ButtonClickEvent = MouseEvent<HTMLButtonElement>;

export enum CallButtonVariant {
    RECTANGULAR = "rectangular", // Rectangular button
    SQUARE = "square", // Square button
    CIRCLE = "circle", // Circle button
    ICON = "icon", // Icon button
    TEXT = "text", // Text button
}

type CallButtonSize = "sm" | "md" | "lg" | "xl";

type CallButtonProps = {
    // Core functionality
    onCall?: (e: ButtonClickEvent) => void;
    onHangup?: (e: ButtonClickEvent) => void;
    disabled?: boolean;
    callStatus?: CallStatus;

    // Variant and styling
    variant?: CallButtonVariant;
    size?: CallButtonSize;
    showText?: boolean;
    text?: string;

    // Tooltip
    tooltip?: string;
    tooltipSide?: "top" | "bottom" | "left" | "right";

    // Custom styling
    className?: string;
};

const variantStyles: Record<CallButtonVariant, string> = {
    [CallButtonVariant.RECTANGULAR]: "w-1/2 h-16 duration-200 flex items-center justify-center group [&_svg]:size-6",
    [CallButtonVariant.SQUARE]: "h-8 w-8",
    [CallButtonVariant.CIRCLE]: "size-icon",
    [CallButtonVariant.ICON]: "size-icon",
    [CallButtonVariant.TEXT]: "h-6 w-6",
};

const sizeStyles: Record<CallButtonSize, string> = {
    sm: "text-sm",
    md: "text-base",
    lg: "text-lg",
    xl: "text-xl",
};

const iconSizes: Record<CallButtonSize, number> = {
    sm: 16,
    md: 20,
    lg: 24,
    xl: 28,
};

export const CallButton = ({
    onCall,
    onHangup,
    disabled = false,
    callStatus,
    variant = CallButtonVariant.RECTANGULAR,
    size = "md",
    showText = false,
    text,
    tooltip,
    tooltipSide = "top",
    className,
}: CallButtonProps) => {
    const { callButtonCooldown, isCallActive } = useCall();
    const { t } = useTranslation();

    // Determine if call is active
    const isActive = callStatus
        ? callStatus === CALL_STATUS.CONNECTED ||
          callStatus === CALL_STATUS.CONNECTING ||
          callStatus === CALL_STATUS.RINGING
        : isCallActive();

    // When call is active but no onHangup: show as disabled call button (dock/dialer keep onHangup and show hang-up)
    const showAsHangUp = isActive && onHangup != null;

    const getIcon = () => {
        const iconSize = iconSizes[size];

        if (showAsHangUp) {
            return callButtonCooldown ? (
                <TbPhoneCalling size={iconSize} className="text-current" />
            ) : (
                <TbPhoneOff size={iconSize} className="text-current" />
            );
        }
        return callButtonCooldown ? (
            <TbPhoneCalling size={iconSize} className="text-current" />
        ) : (
            <TbPhone size={iconSize} className="text-current" />
        );
    };

    const handlePress = useCallback(
        (e: ButtonClickEvent) => {
            if (callButtonCooldown) return;
            if (isActive && !onHangup) return;

            if (isActive) {
                onHangup?.(e);
            } else {
                onCall?.(e);
            }
        },
        [isActive, callButtonCooldown, onCall, onHangup]
    );

    const buttonVariant = showAsHangUp ? "destructive" : "default";
    const isDisabledAsCallOnly = isActive && !onHangup;
    const buttonSize =
        variant === CallButtonVariant.RECTANGULAR
            ? "lg"
            : variant === CallButtonVariant.SQUARE
              ? "sm"
              : variant === CallButtonVariant.CIRCLE
                ? "icon"
                : variant === CallButtonVariant.ICON
                  ? "icon"
                  : variant === CallButtonVariant.TEXT
                    ? "sm"
                    : "icon";

    const buttonClassName = cn(
        variantStyles[variant],
        sizeStyles[size],
        {
            // Styling for inactive state (and active call-only: disabled call button)
            "bg-primary text-primary-foreground border-primary hover:bg-primary/90 hover:border-primary/90 cursor-pointer":
                variant === CallButtonVariant.RECTANGULAR && !showAsHangUp,
            // Styling for active hang-up state (dock/dialer only)
            "bg-destructive text-destructive-foreground hover:bg-destructive/90 cursor-pointer":
                variant === CallButtonVariant.RECTANGULAR && showAsHangUp,
        },
        className
    );

    const buttonContent = (
        <Button
            variant={buttonVariant}
            size={buttonSize}
            onClick={handlePress}
            disabled={disabled || callButtonCooldown || isDisabledAsCallOnly}
            className={buttonClassName}
            aria-label={showAsHangUp ? t("hang_up_call") : t("make_call")}
        >
            {getIcon()}
            {showText && text && <span className="ml-2">{text}</span>}
        </Button>
    );

    // Wrap with tooltip if provided
    if (tooltip) {
        return (
            <Tooltip>
                <TooltipTrigger asChild>{buttonContent}</TooltipTrigger>
                <TooltipContent side={tooltipSide}>{tooltip}</TooltipContent>
            </Tooltip>
        );
    }

    return buttonContent;
};
